import IconArrowDown from '@apollo/icons/default/IconArrowDown.svg';
import IconArrowUp from '@apollo/icons/default/IconArrowUp.svg';
import { IconButton } from '@apollo/orbit';
import * as monacoEditor from 'monaco-editor';
import React, { useCallback, useState } from 'react';

import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { SUPERGRAPH_SCHEMA_ID } from '../../../constants';
import { Violation } from '../../lintCheckList/ruleViolationList/RuleViolationList';

export const LintDiffNavigatorButtons = ({
  editor,
  violations,
  selectedSchema,
}: {
  editor: monacoEditor.editor.IStandaloneDiffEditor;
  violations: GraphQLTypes.Maybe<Violation>[];
  selectedSchema: string;
}) => {
  const [currentViolationInView, setCurrentViolationInView] = useState(0);

  React.useEffect(() => {
    const currentViolationLocations = violations[
      currentViolationInView
    ]?.sourceLocations?.filter((location) =>
      selectedSchema !== SUPERGRAPH_SCHEMA_ID
        ? location.subgraphName === selectedSchema
        : !location.subgraphName,
    );

    if (!isMinLengthArray(1, currentViolationLocations)) return;

    const rangeToReveal = new monacoEditor.Range(
      currentViolationLocations[0].start?.line ?? 0,
      currentViolationLocations[0].start?.column ?? 0,
      currentViolationLocations[0].end?.line ?? 0,
      currentViolationLocations[0].end?.column ?? 0,
    );

    editor.setSelection(rangeToReveal);
    editor.revealRangeInCenter(rangeToReveal);
  }, [currentViolationInView, editor, selectedSchema, violations]);

  const totalViolations = violations?.length || 0;

  const onMoveToNextViolation = useCallback(() => {
    setCurrentViolationInView(
      (currentValue) => (currentValue + 1) % totalViolations,
    );
  }, [totalViolations]);

  const onMoveToPreviousViolation = useCallback(() => {
    setCurrentViolationInView(
      (currentValue) => (currentValue - 1) % totalViolations,
    );
  }, [totalViolations]);

  return (
    <div className="flex items-center">
      <div className="mr-3 text-sm text-neutral">
        {currentViolationInView + 1} of {totalViolations}{' '}
        {format.pluralize(totalViolations, 'violation')}
      </div>
      <Tooltip label="Previous lint violation">
        <IconButton
          aria-label="Previous"
          size="sm"
          className="mr-1"
          variant="secondary"
          isDisabled={currentViolationInView === 0}
          onClick={onMoveToPreviousViolation}
        >
          <IconArrowUp />
        </IconButton>
      </Tooltip>
      <Tooltip label="Next lint violation" placement="top">
        <IconButton
          aria-label="Next"
          size="sm"
          className="mr-1"
          variant="secondary"
          isDisabled={currentViolationInView === totalViolations - 1}
          onClick={onMoveToNextViolation}
        >
          <IconArrowDown />
        </IconButton>
      </Tooltip>
    </div>
  );
};
